import styled from "@emotion/styled";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import { fontFredoka, fontRoboto } from "@product/scmp-sdk";

import { Button } from "scmp-app/components/posties/button";
import { Container as FormLabel } from "scmp-app/components/posties/student-form/form-label/styles";

export const FormContainer = styled.form`
  padding-inline: 24px;

  & .MuiAutocomplete-listbox {
    padding-block: 0;
    li {
      padding: 20px;

      font-weight: 400;
      font-size: 18px;
      font-family: ${fontFredoka};
      line-height: 21.78px;

      background-color: #ffffff;

      &[aria-selected="true"].Mui-focused,
      &[aria-selected="true"],
      &.Mui-focused {
        background-color: #fafafa;
      }
    }
  }
`;

export const StyledFormControl = styled(FormControl)`
  &:not(:last-child) {
    margin-block-end: 29px;
  }
`;

export const HtmlFormLabel = styled(FormLabel)`
  flex-direction: column;
  gap: 0;
  align-items: flex-start;
  img {
    display: block;

    inline-size: 100%;
  }

  *:not(:last-child) {
    margin-block-end: 8px;
  }
`;

export const StyledTextField = styled(TextField)`
  display: block;

  & .MuiInputBase-root {
    padding-block: 18px 20px;
    padding-inline: 20px;

    font-size: 18px;
    font-family: ${fontFredoka};
    line-height: 21.78px;

    background-color: #ffffff;

    &:hover {
      & .MuiOutlinedInput-notchedOutline {
        border-color: #1b4191;
      }

      &.Mui-error {
        background-color: #ffe2e2;
        & .MuiOutlinedInput-notchedOutline {
          border: 1px solid #eb4d42;
        }
      }
    }

    &.MuiInputBase-multiline {
      background-color: #ffffff;
    }
    & .MuiInputBase-input {
      padding-block: 0;
      padding-inline: 0;

      border-radius: inherit;

      transition: background-color 0.3s;
    }

    & .MuiOutlinedInput-notchedOutline {
      border: 1px solid #d0d0d0;

      transition: border-color 0.3s;
    }

    &.Mui-focused {
      & .MuiOutlinedInput-notchedOutline {
        border-color: #1b4191;
        border-width: 1px;
      }
    }

    &.Mui-error {
      background-color: #ffe2e2;
      & .MuiOutlinedInput-notchedOutline {
        border-color: #eb4d42;
        border-width: 1px;
      }

      & .Mui-focused {
        & .MuiOutlinedInput-notchedOutline {
          border-width: 1px;
        }
      }
      & .MuiInputBase-input {
        &::placeholder {
          color: #000000;

          opacity: 1;
        }
      }
    }
  }

  & .MuiFormHelperText-root.Mui-error {
    margin-block-start: 8px;
    margin-inline-start: 0;

    color: #eb4d42;
    font-weight: 400;
    font-size: 14px;
    font-family: ${fontRoboto};
    line-height: 16.41px;
  }
`;

export const StyledAutocomplete = styled(Autocomplete)`
  & .MuiInputBase-root {
    padding-block: 18px 20px;
    padding-inline: 20px;

    background-color: #ffffff;

    & .MuiAutocomplete-input {
      padding: 0;
    }

    & .MuiAutocomplete-endAdornment {
      inset-inline-end: 22px;
    }
  }

  &:hover {
    & .Mui-error {
      & .MuiOutlinedInput-notchedOutline {
        border: 1px solid #eb4d42;
      }

      & .MuiInputBase-input {
        background-color: #ffe2e2;
      }
    }
  }

  & .MuiFormControl-root {
    border-radius: 4px;
  }

  & .Mui-error {
    & .MuiInputBase-root {
      background-color: #ffe2e2;
    }
    & .MuiOutlinedInput-notchedOutline {
      border-color: #eb4d42;
      border-width: 1px;
    }

    & .MuiInputBase-input {
      background-color: #ffe2e2;
    }
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const StyledButton = styled(Button)`
  margin-block-end: 0;
  padding-block: 12px;
  padding-inline: 46px;

  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  ${props => props.theme.breakpoints.up("tablet")} {
    padding-block: 12px;
    padding-inline: 46px;

    font-size: 16px;
    line-height: 20px;
  }
`;

export const StyledFormHelperText = styled(FormHelperText)`
  &,
  &.MuiFormHelperText-root.Mui-error {
    margin-block-start: 8px;
    margin-inline-start: 0;

    color: #eb4d42;
    font-weight: 400;
    font-size: 14px;
    font-family: ${fontRoboto};
    line-height: 16.41px;
  }
`;

export const StyledFormControlLabel = styled(FormControlLabel)`
  &.MuiFormControlLabel-root {
    margin-inline: 0;
    padding-block: 18px 20px;
    padding-inline: 20px;

    border: 1px solid #d0d0d0;
    border-radius: 4px;

    background-color: #ffffff;

    &:not(:last-child) {
      margin-block-end: 8px;
    }

    & .MuiButtonBase-root {
      padding-block: 0;
      padding-inline: 0 16px;
    }

    & .MuiTypography-root {
      color: #666666;
      font-weight: 400;
      font-size: 16px;
      font-family: ${fontFredoka};
      line-height: 19.36px;
    }
  }
`;

export const WordLimit = styled.div`
  margin-block-start: 8px;

  color: #666666;
  font-weight: 600;
  font-size: 16px;
  font-family: ${fontFredoka};
  line-height: 19.36px;
  letter-spacing: 0.5px;
  text-align: end;
`;
